import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Breadcrumb } from '@/components/Breadcrumb'
import { ViewTabs, type ViewTab } from '@/components/ViewTabs'
import { fetchWorkItems } from '@/features/work-items/api'
import { TaskList } from '@/features/work-items/TaskList'
import { TaskPanel } from '@/features/work-items/TaskPanel'
import type { ApiError } from '@/lib/api/client'
import type { WorkItem } from '@/types/work'

type Tab = 'mine' | 'unassigned'

/**
 * The screen a person opens first: what is on their plate, and what nobody has
 * picked up.
 *
 * Two tabs rather than two stacked tables — the second list is a queue to pull
 * from, not a second thing to read every morning.
 */
export function MyWorkPage() {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>('mine')
  const [open, setOpen] = useState<WorkItem | null>(null)

  const mine = useQuery<WorkItem[], ApiError>({
    queryKey: ['work-items', { assignee: 'me' }],
    queryFn: () => fetchWorkItems({ assignee: 'me' }),
    retry: false,
  })

  const everything = useQuery<WorkItem[], ApiError>({
    queryKey: ['work-items', { open: true }],
    queryFn: () => fetchWorkItems({ open: true }),
    retry: false,
  })

  if (mine.isPending || everything.isPending) {
    return <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
  }

  const unassigned = (everything.data ?? []).filter((item) => item.assignee == null)

  const tabs: ViewTab[] = [
    { id: 'mine', label: `${t('work.assignedToYou')} · ${mine.data?.length ?? 0}`, icon: 'inbox' },
    { id: 'unassigned', label: `${t('work.unassigned')} · ${unassigned.length}`, icon: 'list' },
  ]

  const rows = tab === 'mine' ? (mine.data ?? []) : unassigned
  const asOf = tab === 'mine' ? mine.dataUpdatedAt : everything.dataUpdatedAt

  return (
    <div>
      <Breadcrumb trail={[{ label: t('nav.work') }]} />
      <h1 className="text-ink-900 mb-3 text-xl font-semibold tracking-tight">{t('nav.work')}</h1>

      <ViewTabs tabs={tabs} active={tab} onSelect={(id) => setTab(id as Tab)} />

      <div className="pt-5">
        <TaskList items={rows} asOf={asOf} onOpen={(item) => setOpen(item)} showProject />
      </div>

      {open === null ? null : <TaskPanel key={open.id} item={open} onClose={() => setOpen(null)} />}
    </div>
  )
}
